import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiService } from '../services/api';
import { Framework } from '../types/frameworks';

interface FrameworkContextType {
  // Active scoring framework (null = system default)
  activeScoringFramework: Framework | null;
  // All user frameworks
  scoringFrameworks: Framework[];
  // Loading state
  isLoading: boolean;
  // Actions
  setActiveScoringFramework: (framework: Framework | null) => void;
  refreshFrameworks: () => Promise<void>;
}

const FrameworkContext = createContext<FrameworkContextType | undefined>(undefined);

export const useFramework = () => {
  const context = useContext(FrameworkContext);
  if (context === undefined) {
    throw new Error('useFramework must be used within a FrameworkProvider');
  }
  return context;
};

interface FrameworkProviderProps {
  children: ReactNode;
}

export const FrameworkProvider: React.FC<FrameworkProviderProps> = ({ children }) => {
  const [scoringFrameworks, setScoringFrameworks] = useState<Framework[]>([]);
  const [activeScoringFramework, setActiveScoringFrameworkState] = useState<Framework | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const refreshFrameworks = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiService.getFrameworks('scoring');
      const frameworks: Framework[] = response.frameworks || [];
      setScoringFrameworks(frameworks);

      // Find the active framework
      const active = frameworks.find((f: Framework) => f.is_active && f.user_id !== null);
      if (active) {
        setActiveScoringFrameworkState(active);
        localStorage.setItem('active_framework_id', String(active.id));
      } else {
        // Check localStorage for persisted selection
        const storedId = localStorage.getItem('active_framework_id');
        if (storedId) {
          const stored = frameworks.find((f: Framework) => f.id === Number(storedId));
          if (stored) {
            setActiveScoringFrameworkState(stored);
          } else {
            setActiveScoringFrameworkState(null);
            localStorage.removeItem('active_framework_id');
          }
        } else {
          setActiveScoringFrameworkState(null);
        }
      }
    } catch (err) {
      // Silently fail — user may not be authenticated
      setScoringFrameworks([]);
      setActiveScoringFrameworkState(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const setActiveScoringFramework = useCallback((framework: Framework | null) => {
    setActiveScoringFrameworkState(framework);
    if (framework) {
      localStorage.setItem('active_framework_id', String(framework.id));
    } else {
      localStorage.removeItem('active_framework_id');
    }
  }, []);

  // Load frameworks on mount
  useEffect(() => {
    refreshFrameworks();
  }, [refreshFrameworks]);

  return (
    <FrameworkContext.Provider
      value={{
        activeScoringFramework,
        scoringFrameworks,
        isLoading,
        setActiveScoringFramework,
        refreshFrameworks,
      }}
    >
      {children}
    </FrameworkContext.Provider>
  );
};
